if (!customElements.get('related-products-v2')) {
  customElements.define('related-products-v2', class RelatedProductsV2 extends HTMLElement {
    initialized = false;
    apiSectionId = 'api-related-products-v2-sliders';

    constructor() {
      super();
      this.sectionSettings = JSON.parse(this.getAttribute("data-settings").replaceAll("/'", '"'));
      this._handleIsMobileChange = this.handleIsMobileChange.bind(this);

      this.isMobile = window.matchMedia('(max-width: 768px)');
      this.isMobile.addEventListener('change', this._handleIsMobileChange);
    }

    init() {
      if (this.initialized) {
        return;
      }
      this.initialized = true;
      this._handleIsMobileChange(this.isMobile);
    }

    connectedCallback() {
      this.observeElement(this, this.handleInViewport.bind(this));
      const productId = this.getAttribute("data-product-id");
      if (this.sectionSettings.source === 'auto_recommendations' && productId) {
        if (this.sectionSettings.enable_smart_recommendations) {
          this.initSmartRecommendations();
        } else {
          this.initShopifyRecommendations();
        }
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.isMobile.removeEventListener('change', this._handleIsMobileChange);
      this.destroySlider();
    }

    getRecommendationsLimit() {
      const parsed = Number(this.sectionSettings.limit);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
      return 8;
    }

    getShopifyRecommendationsLimit() {
      return Math.min(this.getRecommendationsLimit(), 10);
    }

    hasValue(value) {
      return value != null && value !== '';
    }

    isUsableSlide(slider, productId, usedIds) {
      const id = slider.getAttribute('data-slide-product-id');
      const available = slider.getAttribute('data-slide-product-available') === 'true';
      if (!available || !id || String(id) === String(productId) || usedIds.has(String(id))) {
        return false;
      }
      return true;
    }

    collectSlides(sliders, productId, usedIds, renderSliders, limit) {
      for (const slider of sliders) {
        if (renderSliders.length >= limit) {
          break;
        }
        if (!this.isUsableSlide(slider, productId, usedIds)) {
          continue;
        }
        usedIds.add(String(slider.getAttribute('data-slide-product-id')));
        renderSliders.push(slider);
      }
    }

    shouldUseSlider() {
      if (this.isMobile.matches) {
        return this.sectionSettings.mobile_layout == 'slider';
      }
      return this.sectionSettings.desktop_layout == 'slider';
    }

    createSlider() {
      if (this.mySwiper) {
        return;
      }
      this.mySwiper = new Swiper(this.querySelector('.swiper-container'), {
        slidesPerView: 'auto',
        loop: false,
        speed: 500,
        observer: true,
        observeParents: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
      });
    }

    destroySlider() {
      if (this.mySwiper) {
        this.mySwiper.destroy(true, true);
        this.mySwiper = null;
      }
    }

    refreshSliderIfNeeded() {
      if (!this.initialized) {
        return;
      }
      this.destroySlider();
      if (this.shouldUseSlider()) {
        this.createSlider();
      }
    }

    handleIsMobileChange(e) {
      if (!this.initialized) {
        return;
      }
      this.destroySlider();
      if (this.shouldUseSlider()) {
        this.createSlider();
      }
    }

    initShopifyRecommendations() {
      const productId = this.getAttribute("data-product-id");
      const limit = this.getShopifyRecommendationsLimit();

      const productsWrapper = this.querySelector("[data-swiper-wrapper]");
      if (!productId || !productsWrapper) {
        return;
      }

      const route = window.theme.routes.product_recommendations_url || '/recommendations/products/';
      const requestUrl = `${route}?section_id=related-products-v2&limit=${limit}&product_id=${productId}`;

      themeVendor.axios
        .get(requestUrl)
        .then((response) => {
          const fresh = document.createElement('div');
          fresh.innerHTML = response.data;

          const inner = fresh.querySelector('[data-swiper-wrapper]');
          if (!inner) {
            return;
          }

          productsWrapper.innerHTML = inner.innerHTML;
          this.refreshSliderIfNeeded();
        })
        .catch(function (error) {
          console.warn(error);
        });
    }

    // Smart recommendations.
    initSmartRecommendations() {
      const productId = this.getAttribute("data-product-id");
      const collectionHandle = this.getAttribute("data-collection-handle");
      const productType = this.getAttribute("data-product-type");
      this.productsWrapper = this.querySelector("[data-swiper-wrapper]");
      if (!this.productsWrapper) {
        return;
      }

      if (this.hasValue(collectionHandle) || this.hasValue(productType)) {
        this.loadSmartRecommendations(collectionHandle, productType, productId);
      } else {
        console.warn('No collection handle or product type found');
        this.initShopifyRecommendations();
      }
    }

    // Load smart recommendations from the request url.
    async loadSmartRecommendations(collectionHandle, productType, productId) {
      const limit = this.getRecommendationsLimit();
      const renderSliders = [];
      const usedIds = new Set();
      const collectionRequestUrl = `${window.theme.routes.root_url}collections/${collectionHandle}?section_id=${this.apiSectionId}&sort_by=best-selling`;
      const productTypeRequestUrl = `${window.theme.routes.root_url}collections/all-products?section_id=${this.apiSectionId}&sort_by=best-selling&filter.p.product_type=${encodeURIComponent(productType)}`;

      try {
        if (this.hasValue(collectionHandle)) {
          const { sliders } = await this.requestSmartRecommendations(collectionRequestUrl);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }

        if (this.hasValue(productType) && renderSliders.length < limit) {
          const { sliders } = await this.requestSmartRecommendations(productTypeRequestUrl);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }

        if (renderSliders.length < limit) {
          const { sliders } = await this.requestShopifyRecommendations(productId);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }
      } catch (error) {
        console.warn(error);
      }

      this.productsWrapper.innerHTML = renderSliders.map(slider => slider.outerHTML).join('');
      this.refreshSliderIfNeeded();
    }

    async requestSmartRecommendations(requestUrl) {
      try {
        const response = await fetch(requestUrl);
        if (!response.ok) {
          console.warn('Smart recommendations request failed', response.status, requestUrl);
          return { sliders: [] };
        }
        const data = await response.text();
        const fresh = document.createElement('div');
        fresh.innerHTML = data;
        return {
          sliders: [...fresh.querySelectorAll('[data-related-products-slide]')],
        };
      } catch (error) {
        console.warn('Smart recommendations request failed', error);
        return { sliders: [] };
      }
    }

    async requestShopifyRecommendations(productId) {
      try {
        const route = window.theme.routes.product_recommendations_url || '/recommendations/products/';
        const limit = this.getShopifyRecommendationsLimit();
        const requestUrl = `${route}?section_id=${this.apiSectionId}&limit=${limit}&product_id=${productId}`;
        const response = await fetch(requestUrl);
        if (!response.ok) {
          console.warn('Shopify recommendations request failed', response.status, requestUrl);
          return { sliders: [] };
        }
        const data = await response.text();
        const fresh = document.createElement('div');
        fresh.innerHTML = data;
        return {
          sliders: [...fresh.querySelectorAll('[data-related-products-slide]')],
        };
      } catch (error) {
        console.warn('Shopify recommendations request failed', error);
        return { sliders: [] };
      }
    }

    handleInViewport() {
      this.init();
    }

    observeElement(element, callback) {
      const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback();
            observerInstance.unobserve(entry.target);
          }
        });
      }, {
        root: null,
        threshold: 0.5
      });
      if (this.isElementInViewport(element)) {
        callback();
        return;
      }
      observer.observe(element);
      this.observer = observer;
    }

    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= -el.offsetHeight / 2 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + el.offsetHeight / 2
      );
    }
  });
}
